// BA/BS Form Raporu — TR mevzuat zorunluluğu (Form Ba / Form Bs).
// 5.000 TL üstü alış (Ba) / satış (Bs) tedarikçi/cari bazlı toplama.
import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { Button, ListPageHeader, FormField, EmptyState } from '../components/ui';
import { toast } from '../components/Toast';
import { fmtAmount } from '../utils/numberFormat';

export default function BABSReportPage() {
  const now = new Date();
  const [type, setType] = useState('ba'); // ba | bs
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [threshold, setThreshold] = useState(5000);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/purchase-insights/babs', { params: { type, year, month, threshold } });
      if (r.data.success) setData(r.data.data);
    } catch (e) { toast.error('Rapor alınamadı'); }
    finally { setLoading(false); }
  }, [type, year, month, threshold]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  const exportCsv = () => {
    if (!data?.items?.length) return;
    const headers = ['VKN/TC', 'Vergi Dairesi', 'Cari Adı', 'Fatura Sayısı', 'Toplam Tutar (TL)'];
    const rows = data.items.map(it => [
      it.tax_number || it.id_number || '',
      it.tax_office || '',
      '"' + (it.title || '').replace(/"/g, '""') + '"',
      it.invoice_count,
      parseFloat(it.total_amount).toFixed(2),
    ]);
    const csv = '﻿' + [headers, ...rows].map(r => r.join(';')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${data.form_type}_${year}_${String(month).padStart(2, '0')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="stocks-page" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <ListPageHeader title="BA / BS Formu" unit="" count={data?.items?.length || 0}
        actions={<Button size="sm" onClick={exportCsv} disabled={!data?.items?.length}>📥 CSV İndir</Button>} />

      <div style={{ padding: 16, overflowY: 'auto', flex: 1 }}>
        {/* Filtre */}
        <div style={S.filterBox}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, alignItems: 'flex-end' }}>
            <FormField label="Form Tipi">
              <select value={type} onChange={e => setType(e.target.value)} style={{ minWidth: 180 }}>
                <option value="ba">BA — Alış Faturaları (Tedarikçi)</option>
                <option value="bs">BS — Satış Faturaları (Müşteri)</option>
              </select>
            </FormField>
            <FormField label="Yıl">
              <input type="number" value={year} onChange={e => setYear(parseInt(e.target.value))} style={{ width: 90 }} />
            </FormField>
            <FormField label="Ay">
              <select value={month} onChange={e => setMonth(parseInt(e.target.value))} style={{ width: 120 }}>
                {[1,2,3,4,5,6,7,8,9,10,11,12].map(m => <option key={m} value={m}>{m.toString().padStart(2,'0')}</option>)}
              </select>
            </FormField>
            <FormField label="Eşik (TL)">
              <input type="number" value={threshold} onChange={e => setThreshold(parseFloat(e.target.value))} style={{ width: 120 }} />
            </FormField>
            <Button size="sm" onClick={fetchReport} loading={loading}>Yenile</Button>
          </div>
        </div>

        {/* Özet */}
        {data && (
          <div style={S.summary}>
            <div style={S.summaryItem}>
              <div style={S.summaryLabel}>Form</div>
              <div style={{ ...S.summaryValue, color: 'var(--accent-primary)' }}>{data.form_type}</div>
            </div>
            <div style={S.summaryItem}>
              <div style={S.summaryLabel}>Dönem</div>
              <div style={S.summaryValue}>{data.period.year}/{String(data.period.month).padStart(2,'0')}</div>
            </div>
            <div style={S.summaryItem}>
              <div style={S.summaryLabel}>Cari Sayısı</div>
              <div style={S.summaryValue}>{data.total_count}</div>
            </div>
            <div style={S.summaryItem}>
              <div style={S.summaryLabel}>Toplam Tutar</div>
              <div style={{ ...S.summaryValue, color: 'var(--success)' }}>{fmtAmount(data.total_amount)} ₺</div>
            </div>
          </div>
        )}

        {/* Tablo */}
        {loading ? (
          <div style={{ padding: 20, color: 'var(--text-tertiary)', fontSize: 'var(--text-sm)' }}>Yükleniyor...</div>
        ) : !data?.items?.length ? (
          <EmptyState icon="📋" title="Bu dönem için kayıt yok" description={`${data?.form_type || 'BA/BS'} formuna giren ${threshold} TL üstü kayıt bulunamadı.`} />
        ) : (
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-secondary)', borderRadius: 'var(--r-md)', overflow: 'hidden' }}>
            <table style={{ width: '100%', fontSize: 'var(--text-sm)', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'var(--bg-tertiary)', borderBottom: '1px solid var(--border-secondary)' }}>
                  <th style={S.th}>#</th>
                  <th style={S.th}>VKN / TC</th>
                  <th style={S.th}>Vergi Dairesi</th>
                  <th style={S.th}>Cari Adı</th>
                  <th style={{ ...S.th, textAlign: 'center' }}>Fatura</th>
                  <th style={{ ...S.th, textAlign: 'right' }}>Tutar (TL)</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((it, i) => (
                  <tr key={it.account_id} style={{ borderBottom: '1px solid var(--border-secondary)' }}>
                    <td style={S.td}>{i + 1}</td>
                    <td style={{ ...S.td, fontFamily: 'JetBrains Mono, monospace' }}>{it.tax_number || it.id_number || '—'}</td>
                    <td style={S.td}>{it.tax_office || '—'}</td>
                    <td style={S.td}><strong>{it.title}</strong> <span style={{ color: 'var(--text-tertiary)', fontSize: 'var(--text-xs)' }}>({it.code})</span></td>
                    <td style={{ ...S.td, textAlign: 'center' }}>{it.invoice_count}</td>
                    <td style={{ ...S.td, textAlign: 'right', fontWeight: 600, color: 'var(--accent-primary)' }}>{fmtAmount(it.total_amount)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ background: 'var(--bg-tertiary)', borderTop: '2px solid var(--accent-primary)', fontWeight: 700 }}>
                  <td colSpan={4} style={{ ...S.td, textAlign: 'right' }}>TOPLAM:</td>
                  <td style={{ ...S.td, textAlign: 'center' }}>{data.items.reduce((a,b)=>a+b.invoice_count,0)}</td>
                  <td style={{ ...S.td, textAlign: 'right', color: 'var(--success)' }}>{fmtAmount(data.total_amount)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}

        <div style={{ marginTop: 14, fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)', lineHeight: 1.6 }}>
          <strong>Not:</strong> BA / BS formları aylık olarak GİB'e bildirilir. Eşik (varsayılan 5.000 TL) yıldan yıla değişebilir — Maliye tebliğine göre güncelleyin.
          CSV çıktısını <strong>Microsoft Excel</strong> veya <strong>e-Beyanname</strong> sistemine yükleyebilirsiniz.
        </div>
      </div>
    </div>
  );
}

const S = {
  filterBox: {
    background: 'var(--bg-card)',
    border: '1px solid var(--border-secondary)',
    borderRadius: 'var(--r-md)',
    padding: 14,
    marginBottom: 14,
    boxShadow: 'var(--shadow-xs)',
  },
  summary: {
    display: 'flex',
    background: 'var(--bg-card)',
    border: '1px solid var(--border-secondary)',
    borderRadius: 'var(--r-md)',
    padding: 14,
    gap: 16,
    marginBottom: 14,
    flexWrap: 'wrap',
  },
  summaryItem: { flex: 1, minWidth: 120 },
  summaryLabel: { fontSize: '0.65rem', color: 'var(--text-tertiary)', textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.04em' },
  summaryValue: { fontSize: 'var(--text-xl)', fontWeight: 700, color: 'var(--text-primary)', marginTop: 4, fontFamily: 'JetBrains Mono, monospace' },
  th: { padding: '10px 12px', textAlign: 'left', fontSize: 'var(--text-xs)', fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em' },
  td: { padding: '10px 12px' },
};
